import {
  createSealedVisualizationProof,
  isVisualizationRecordEligibleForInstance,
  SealedVisualizationProof,
  VisualizationCounter,
  VisualizationProofOutboxState,
  VisualizationProofStore,
} from './visualization-proof-outbox.types';

const DATABASE_NAME = 'admefy-player-visualization-outbox';
const DATABASE_VERSION = 1;
const COUNTERS_STORE = 'counters';
const PROOFS_STORE = 'proofs';
const HEALTH_CHECK_KEY = '__health_check__';

export const DEFAULT_INDEXED_DB_OPEN_TIMEOUT_MS = 2500;
export const DEFAULT_INDEXED_DB_TRANSACTION_TIMEOUT_MS = 2500;

export class IndexedDbTransactionStateUnknownError extends Error {
  constructor() {
    super('IndexedDB transaction commit state is unknown');
    this.name = 'IndexedDbTransactionStateUnknownError';
  }
}

export class IndexedDbVisualizationProofStore implements VisualizationProofStore {
  readonly kind = 'indexed-db' as const;

  private constructor(
    private readonly database: IDBDatabase,
    private readonly transactionTimeoutMs: number,
  ) {
    this.database.onversionchange = () => {
      this.database.close();
    };
  }

  static async open(
    indexedDb: IDBFactory,
    openTimeoutMs: number = DEFAULT_INDEXED_DB_OPEN_TIMEOUT_MS,
    transactionTimeoutMs: number = DEFAULT_INDEXED_DB_TRANSACTION_TIMEOUT_MS,
  ): Promise<IndexedDbVisualizationProofStore> {
    const database = await openDatabase(indexedDb, openTimeoutMs);
    const store = new IndexedDbVisualizationProofStore(database, transactionTimeoutMs);

    try {
      await store.healthCheck();
      return store;
    } catch (error) {
      store.close();
      throw error;
    }
  }

  async incrementCounter(counter: VisualizationCounter): Promise<void> {
    const transaction = this.database.transaction(COUNTERS_STORE, 'readwrite');
    const store = transaction.objectStore(COUNTERS_STORE);
    const request = store.get(counter.key);

    request.onsuccess = () => {
      const current = request.result as VisualizationCounter | undefined;
      store.put(
        current
          ? {
              ...current,
              identifier: counter.identifier,
              visualizations: current.visualizations + counter.visualizations,
              updatedAt: counter.updatedAt,
            }
          : counter,
      );
    };

    await transactionCompletion(transaction, this.transactionTimeoutMs);
  }

  async importState(state: VisualizationProofOutboxState): Promise<void> {
    if (state.counters.length === 0 && state.proofs.length === 0) {
      return;
    }

    const transaction = this.database.transaction([COUNTERS_STORE, PROOFS_STORE], 'readwrite');
    const countersStore = transaction.objectStore(COUNTERS_STORE);
    const proofsStore = transaction.objectStore(PROOFS_STORE);

    for (const proof of state.proofs) {
      proofsStore.put(proof);
    }

    for (const counter of state.counters) {
      const request = countersStore.get(counter.key);
      request.onsuccess = () => {
        const current = request.result as VisualizationCounter | undefined;
        countersStore.put(
          current
            ? {
                ...current,
                identifier: counter.identifier,
                visualizations: current.visualizations + counter.visualizations,
                updatedAt: Math.max(current.updatedAt, counter.updatedAt),
              }
            : counter,
        );
      };
    }

    await transactionCompletion(transaction, this.transactionTimeoutMs);
  }

  async exportState(): Promise<VisualizationProofOutboxState> {
    const transaction = this.database.transaction([COUNTERS_STORE, PROOFS_STORE], 'readonly');
    const countersRequest = transaction.objectStore(COUNTERS_STORE).getAll();
    const proofsRequest = transaction.objectStore(PROOFS_STORE).getAll();
    const [counters, proofs] = await Promise.all([
      requestResult<VisualizationCounter[]>(countersRequest),
      requestResult<SealedVisualizationProof[]>(proofsRequest),
      transactionCompletion(transaction, this.transactionTimeoutMs),
    ]);

    return {
      counters: counters.map((counter) => ({ ...counter })),
      proofs: proofs.map((proof) => ({ ...proof })),
    };
  }

  async sealCounters(limit: number, instanceId?: string): Promise<void> {
    if (limit <= 0) {
      return;
    }

    const transaction = this.database.transaction([COUNTERS_STORE, PROOFS_STORE], 'readwrite');
    const countersStore = transaction.objectStore(COUNTERS_STORE);
    const proofsStore = transaction.objectStore(PROOFS_STORE);
    const request = countersStore.openCursor();
    let sealedCounters = 0;

    request.onsuccess = () => {
      const cursor = request.result;

      if (!cursor || sealedCounters >= limit) {
        return;
      }

      const counter = cursor.value as VisualizationCounter;
      if (!isVisualizationRecordEligibleForInstance(counter, instanceId)) {
        cursor.continue();
        return;
      }

      proofsStore.put(createSealedVisualizationProof(counter));
      cursor.delete();
      sealedCounters += 1;
      cursor.continue();
    };

    await transactionCompletion(transaction, this.transactionTimeoutMs);
  }

  async listProofs(limit: number, instanceId?: string): Promise<SealedVisualizationProof[]> {
    if (limit <= 0) {
      return [];
    }

    const transaction = this.database.transaction(PROOFS_STORE, 'readonly');
    const request = transaction.objectStore(PROOFS_STORE).openCursor();
    const proofs: SealedVisualizationProof[] = [];

    request.onsuccess = () => {
      const cursor = request.result;

      if (!cursor || proofs.length >= limit) {
        return;
      }

      const proof = cursor.value as SealedVisualizationProof;
      if (isVisualizationRecordEligibleForInstance(proof, instanceId)) {
        proofs.push({ ...proof });
      }
      cursor.continue();
    };

    await transactionCompletion(transaction, this.transactionTimeoutMs);
    return proofs;
  }

  async deleteProofs(proofIds: string[]): Promise<void> {
    if (proofIds.length === 0) {
      return;
    }

    const transaction = this.database.transaction(PROOFS_STORE, 'readwrite');
    const store = transaction.objectStore(PROOFS_STORE);

    for (const proofId of proofIds) {
      store.delete(proofId);
    }

    await transactionCompletion(transaction, this.transactionTimeoutMs);
  }

  async clear(): Promise<void> {
    const transaction = this.database.transaction([COUNTERS_STORE, PROOFS_STORE], 'readwrite');
    transaction.objectStore(COUNTERS_STORE).clear();
    transaction.objectStore(PROOFS_STORE).clear();
    await transactionCompletion(transaction, this.transactionTimeoutMs);
  }

  close(): void {
    this.database.close();
  }

  private async healthCheck(): Promise<void> {
    const transaction = this.database.transaction(COUNTERS_STORE, 'readwrite');
    const store = transaction.objectStore(COUNTERS_STORE);
    const healthCounter: VisualizationCounter = {
      key: HEALTH_CHECK_KEY,
      mediaCampaignId: HEALTH_CHECK_KEY,
      mediaId: HEALTH_CHECK_KEY,
      identifier: HEALTH_CHECK_KEY,
      date: new Date(0).toISOString(),
      visualizations: 0,
      updatedAt: Date.now(),
    };

    store.put(healthCounter);
    store.delete(HEALTH_CHECK_KEY);
    await transactionCompletion(transaction, this.transactionTimeoutMs);
  }
}

function openDatabase(indexedDb: IDBFactory, timeoutMs: number): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const request = indexedDb.open(DATABASE_NAME, DATABASE_VERSION);
    const timeout = setTimeout(() => {
      if (settled) {
        return;
      }

      settled = true;
      reject(new Error('IndexedDB open timed out'));
    }, timeoutMs);

    request.onupgradeneeded = () => {
      const database = request.result;

      if (!database.objectStoreNames.contains(COUNTERS_STORE)) {
        database.createObjectStore(COUNTERS_STORE, { keyPath: 'key' });
      }

      if (!database.objectStoreNames.contains(PROOFS_STORE)) {
        database.createObjectStore(PROOFS_STORE, { keyPath: 'proofId' });
      }
    };

    request.onblocked = () => {
      finishWithError(new Error('IndexedDB open was blocked'));
    };

    request.onerror = () => {
      finishWithError(request.error ?? new Error('IndexedDB open failed'));
    };

    request.onsuccess = () => {
      if (settled) {
        request.result.close();
        return;
      }

      settled = true;
      clearTimeout(timeout);
      resolve(request.result);
    };

    function finishWithError(error: Error): void {
      if (settled) {
        return;
      }

      settled = true;
      clearTimeout(timeout);
      reject(error);
    }
  });
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function transactionCompletion(
  transaction: IDBTransaction,
  timeoutMs: number = DEFAULT_INDEXED_DB_TRANSACTION_TIMEOUT_MS,
): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let timedOut = false;
    const timeout = setTimeout(() => {
      if (settled) {
        return;
      }

      timedOut = true;

      try {
        transaction.abort();
      } catch {
        // The transaction may have committed while its completion event was still queued.
        finishWithError(new IndexedDbTransactionStateUnknownError());
        return;
      }

      finishWithError(new Error('IndexedDB transaction timed out'));
    }, timeoutMs);

    transaction.oncomplete = () => {
      if (settled) {
        return;
      }

      settled = true;
      clearTimeout(timeout);
      resolve();
    };
    transaction.onabort = () => {
      finishWithError(
        timedOut
          ? new Error('IndexedDB transaction timed out')
          : (transaction.error ?? new Error('IndexedDB transaction aborted')),
      );
    };
    transaction.onerror = () => {
      try {
        transaction.abort();
      } catch {
        // Some implementations abort automatically after a request error.
      }

      finishWithError(transaction.error ?? new Error('IndexedDB transaction failed'));
    };

    function finishWithError(error: Error): void {
      if (settled) {
        return;
      }

      settled = true;
      clearTimeout(timeout);
      reject(error);
    }
  });
}
